import { arktypeResolver } from '@hookform/resolvers/arktype';
import { Box } from '@mui/material';
import { QueryStatus } from '@reduxjs/toolkit/query';
import React from 'react';
import { Navigate, useParams } from 'react-router';

import { useCreateFiatAccountMutation } from '@/api/v1';
import { LoadingButton } from '@/components/molecules/LoadingButton';
import { useCustomer } from '@/hooks/useCustomer';
import { useFormWithErrors } from '@/hooks/useFormWithErrors';
import {
  IBANSwiftValidationException,
  useIBANAndSwiftValidation,
} from '@/hooks/useIBANAndSwiftValidation';
import { PaymentMethod, usePaymentMethodsByCountry } from '@/hooks/usePaymentMethods';
import { useToast } from '@/hooks/useToast';
import { useTranslator } from '@/hooks/useTranslator';
import {
  FiatPaymentForm,
  isFiatSwiftPaymentForm,
  paymentMethodFiatSchema,
} from '@/schemas/paymentMethod';
import { FiatAccountDetailFields } from '@/Screens/Dashboard/Contacts/FiatAccountDetailFields';
import { emptyByFiatAccountType } from '@/Screens/Dashboard/Flows/TransferFlow/helpers';
import { Contact, ContactType, CreateFiatAccountRequest, SelfContact } from '@/types/contacts';
import { CountryType } from '@/types/country';
import { isCountryType } from '@/types/country.ext';
import { FiatAccountType } from '@/types/fiatAccountType';
import { isFiatAccountType } from '@/types/fiatAccountType.ext';

interface Props {
  readonly redirectPath: string;
  readonly contact: Contact | SelfContact | undefined;

  onCreated(id: string): void;
}

export const PaymentMethodFiat: React.FC<Props> = (props: Props): React.ReactElement => {
  const { onCreated, redirectPath, contact } = props;
  const customer = useCustomer();
  const tr = useTranslator();
  const toast = useToast();
  const { country, accountType, contactId = null } = useParams();

  // We guard the values to be able to initialize the form if needed. Don't worry, we validate and redirect
  // if both country or accountType are invalid.
  const guardedCountry = country && isCountryType(country) ? country : CountryType.US;
  const guardedAccountType =
    accountType && isFiatAccountType(accountType) ? accountType : FiatAccountType.ach;

  const guardedPaymentMethod = usePaymentMethodsByCountry(guardedCountry);
  const validateIbanAndSwift = useIBANAndSwiftValidation();

  const [createFiatAccount, mutation] = useCreateFiatAccountMutation();

  const form = useFormWithErrors<FiatPaymentForm>({
    defaultValues: emptyByFiatAccountType(guardedAccountType, guardedCountry, contactId),
    resolver: arktypeResolver(paymentMethodFiatSchema),
  });
  const { handleSubmit, setValue, setError } = form;

  const onSubmit = React.useCallback(
    async (form: FiatPaymentForm): Promise<void> => {
      if (!customer.beneficiaryId) {
        console.warn('missing beneficiaryId. Cannot proceed with creating the fiat account.');
        return;
      } else if (!contact) {
        console.warn('missing contact. Cannot proceed with creating the fiat account.');
        return;
      }

      try {
        if (isFiatSwiftPaymentForm(form)) {
          await validateIbanAndSwift(form.iban, form.swiftCode, form.country);
          createFiatAccount(CreateFiatAccountRequest.build(customer.beneficiaryId, contact, form));
        } else {
          createFiatAccount(CreateFiatAccountRequest.build(customer.beneficiaryId, contact, form));
        }
      } catch (error: unknown) {
        if (error instanceof IBANSwiftValidationException) {
          setError(error.field, {
            type: 'manual',
            message: tr(error.message, ...error.parameters),
          });
          return;
        } else {
          toast.show(tr('AN_ERROR_OCCURRED'), 'error');
          console.warn(error);
        }
      }
    },
    [customer.beneficiaryId, contact, validateIbanAndSwift, createFiatAccount, setError, tr, toast],
  );

  React.useEffect((): void => {
    // FIXME: do we really need this? Why not just reset the form?
    if (contact?.entity?.type === ContactType.company) {
      setValue('recipientType', 'company');
      setValue('firstName', contact.entity.legalEntityName);
    } else if (contact?.entity?.type === ContactType.individual) {
      setValue('recipientType', 'individual');
      setValue('firstName', contact.entity.firstName);
      setValue('lastName', contact.entity.lastName);
    }
  }, [setValue, contact]);

  React.useEffect((): void => {
    switch (mutation.status) {
      case QueryStatus.fulfilled:
        mutation.reset();
        onCreated(mutation.data.id);
        break;
      case QueryStatus.rejected:
        toast.show(tr('AN_ERROR_OCCURRED'), 'error');
        break;
      default:
    }
  }, [mutation, toast, onCreated, tr]);

  // Invalid country or accountType.
  if (
    !isCountryType(country) ||
    !isFiatAccountType(accountType) ||
    !guardedPaymentMethod.find(
      (method: PaymentMethod): boolean => method.accountType === accountType,
    )
  ) {
    return <Navigate to={redirectPath} replace={true} />;
  }

  return (
    <Box component="form" name="payment-method-fiat" onSubmit={handleSubmit(onSubmit)}>
      <Box mt={2}>
        <FiatAccountDetailFields form={form} />
        <Box mt={6}>
          <LoadingButton
            type="submit"
            variant="contained"
            fullWidth={true}
            loading={mutation.status === QueryStatus.pending}>
            {tr('ADD_CONTACT')}
          </LoadingButton>
        </Box>
      </Box>
    </Box>
  );
};
